import { Play } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import type { ScenarioSummary } from "@/lib/scenarios";
import { CapBadge } from "./atoms";

type StartCardProps = {
  scenario: ScenarioSummary;
};

/**
 * Fresh-user "Start here" hero. Shown on the Skills home when the
 * learner has no IN_PROGRESS / READY_FOR_CHECKPOINT scenarios — i.e.
 * day one. Surfaces the first AVAILABLE scenario in stage order with
 * the same visual weight as `ContinueCard` so the user lands directly
 * on a "next thing to do" instead of an empty journal.
 *
 * Visually parallel to ContinueCard but with a "Start" CTA and a
 * distinct badge ("Начни отсюда").
 */
export function StartCard({ scenario }: StartCardProps) {
  const t = useT();
  return (
    <div
      className="relative overflow-hidden rounded-[var(--r-lg)] border bg-surface p-[18px]"
      style={{
        borderColor: "var(--scen-solid)",
        borderWidth: 1.5,
        background: "linear-gradient(180deg, var(--scen-bg) 0%, var(--surface) 60%)",
      }}
    >
      <div className="mb-2 flex items-center gap-2">
        <CapBadge sm solid>
          {t("home_skills.start_here")}
        </CapBadge>
        <span className="font-mono text-[11px] text-text-3">
          {scenario.level} · {t("skills.detail_progress_minutes", { n: scenario.estimatedMinutes.toString() })}
        </span>
      </div>
      <h2 className="mb-1 text-[19px] font-bold tracking-tight text-text">{scenario.title}</h2>
      <p className="mb-4 text-[13px] leading-relaxed text-text-2">{scenario.promise}</p>

      <Button asChild variant="scenario" size="block">
        <Link to={`/skills/${scenario.slug}`}>
          <Play strokeWidth={2} />
          {t("home_skills.start_cta")}
        </Link>
      </Button>
    </div>
  );
}
